import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CommunityAssessmentData } from '@/types/entities/clients/assessment';

interface CommunityProfileProps {
  data: Partial<CommunityAssessmentData>;
  onUpdate: (data: Partial<CommunityAssessmentData>) => void;
  tier: 'silver' | 'gold' | 'custom';
}

export const Step_1_CommunityProfile: React.FC<CommunityProfileProps> = ({ data, onUpdate }) => {
  const updateField = (field: string, value: string | number) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      onUpdate({
        ...data,
        [parent]: {
          ...((data[parent as keyof CommunityAssessmentData] as Record<string, unknown>) || {}),
          [child]: value,
        },
      });
    } else {
      onUpdate({ ...data, [field]: value });
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Tell us about your community</h3>
        <p className="text-gray-600">
          This basic information helps us understand your community's structure and needs
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Community Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Community Details</CardTitle>
            <CardDescription>Basic information about your community</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="communityName">Community Name *</Label>
              <Input
                id="communityName"
                value={data.communityName || ''}
                onChange={(e) => updateField('communityName', e.target.value)}
                placeholder="e.g., Sunset Hills HOA"
                required
              />
            </div>

            <div>
              <Label htmlFor="communityType">Community Type *</Label>
              <Select
                value={data.communityType || ''}
                onValueChange={(value) => updateField('communityType', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select community type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hoa">Homeowners Association (HOA)</SelectItem>
                  <SelectItem value="landowner">Land Owner Association</SelectItem>
                  <SelectItem value="condo">Condominium Association</SelectItem>
                  <SelectItem value="townhome">Townhome Community</SelectItem>
                  <SelectItem value="cooperative">Housing Cooperative</SelectItem>
                  <SelectItem value="planned_community">Planned Community</SelectItem>
                  <SelectItem value="mobile_home">Mobile Home Community</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="totalUnits">Total Units/Parcels *</Label>
              <Select
                value={data.totalUnits || ''}
                onValueChange={(value) => updateField('totalUnits', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select community size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-25">1-25 units</SelectItem>
                  <SelectItem value="26-50">26-50 units</SelectItem>
                  <SelectItem value="51-100">51-100 units</SelectItem>
                  <SelectItem value="101-200">101-200 units</SelectItem>
                  <SelectItem value="201-400">201-400 units</SelectItem>
                  <SelectItem value="401-800">401-800 units</SelectItem>
                  <SelectItem value="800+">800+ units</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="establishedYear">Year Established</Label>
              <Input
                id="establishedYear"
                type="number"
                value={data.establishedYear || ''}
                onChange={(e) => updateField('establishedYear', parseInt(e.target.value))}
                placeholder="e.g., 1995"
                min="1900"
                max={new Date().getFullYear()}
              />
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Your Contact Information</CardTitle>
            <CardDescription>How we'll reach out for your discovery call</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="contactName">Your Name *</Label>
              <Input
                id="contactName"
                value={data.contactName || ''}
                onChange={(e) => updateField('contactName', e.target.value)}
                placeholder="e.g., John Smith"
                required
              />
            </div>

            <div>
              <Label htmlFor="contactEmail">Email Address *</Label>
              <Input
                id="contactEmail"
                type="email"
                value={data.contactEmail || ''}
                onChange={(e) => updateField('contactEmail', e.target.value)}
                placeholder="john@example.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="contactPhone">Phone Number</Label>
              <Input
                id="contactPhone"
                type="tel"
                value={data.contactPhone || ''}
                onChange={(e) => updateField('contactPhone', e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Location Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Community Location</CardTitle>
          <CardDescription>
            Location helps us understand local regulations and opportunities
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="state">State</Label>
              <Input
                id="state"
                value={data.location?.state || ''}
                onChange={(e) => updateField('location.state', e.target.value)}
                placeholder="e.g., Montana"
              />
            </div>
            <div>
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={data.location?.city || ''}
                onChange={(e) => updateField('location.city', e.target.value)}
                placeholder="e.g., Billings"
              />
            </div>
            <div>
              <Label htmlFor="zipCode">ZIP Code</Label>
              <Input
                id="zipCode"
                value={data.location?.zipCode || ''}
                onChange={(e) => updateField('location.zipCode', e.target.value)}
                placeholder="e.g., 59101"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          <strong>Why we need this information:</strong> These details help us prepare a
          personalized discovery call agenda and ensure we discuss opportunities most relevant to
          your community type and size.
        </p>
      </div>
    </div>
  );
};
